import React, { useState, useEffect } from "react";
import ParagraphTag from "../../reusebale/Paragraph";
import ReactPaginate from "react-paginate";
import NoData from "../../reusebale/noData";
import { ArrowUpDown, ArrowDownUp, ArrowRight } from "lucide-react";
import db from "../../../db/index.json";
import BaseUrl from "../../../constants/BaseUrl";
import { useSelector } from "react-redux";
import LeaseTableLoader from "../../skeleton-loader/lease-table";

const TenantsTable = ({ onViewDetails, search }) => {
  const { loginSuccess } = useSelector((state) => state.auth);
  const { tenants } = db;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const GetAllTenants = () => {
    setLoading(true);
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

    fetch(
      `${BaseUrl}tenant/getAllTenantByLanlordId/${loginSuccess.user._id}`,
      { method: "GET", headers: myHeaders, redirect: "follow" }
    )
      .then((res) => res.json())
      .then((result) => {
        setLoading(false);
        if (result.success) setData(result.data);
      })
      .catch((error) => { console.error(error); setLoading(false); });
  };

  useEffect(() => {
    GetAllTenants();
  }, []);

  // Unique property names for filter dropdown
  const propertyOptions = [
    "all",
    ...Array.from(new Set(data.map((t) => t.propertyName).filter(Boolean))),
  ];

  const requestSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  const SortIcon = ({ colKey }) => {
    if (sortConfig.key !== colKey) return <ArrowUpDown className="cursor-pointer" size={13} color="#6F6F6F" onClick={() => requestSort(colKey)} />;
    return sortConfig.direction === "asc"
      ? <ArrowUpDown className="cursor-pointer" size={13} color="#6F6F6F" onClick={() => requestSort(colKey)} />
      : <ArrowDownUp className="cursor-pointer" size={13} color="#6F6F6F" onClick={() => requestSort(colKey)} />;
  };

  const filteredData = data
    .filter((item) => {
      const nameMatch = `${item.firstName} ${item.lastName}`.toLowerCase().includes((search || "").toLowerCase());
      const propMatch = selectedProperty === "all" || item.propertyName === selectedProperty;
      return nameMatch && propMatch;
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      const aVal = a[sortConfig.key] ?? "";
      const bVal = b[sortConfig.key] ?? "";
      return sortConfig.direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

  const paginatedData = filteredData.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  return (
    <div className="h-full">
      <div className="bg-white w-full h-[87%]">
        <div className="w-[95%] mx-auto">
          <div className="flex items-center h-[12%] justify-between py-4">
            <ParagraphTag
              content={tenants.heading}
              classes="text-darkColor font-popinsSemiBold text-lg"
            />
            {/* Property filter */}
            <select
              value={selectedProperty}
              onChange={(e) => { setSelectedProperty(e.target.value); setCurrentPage(0); }}
              className="text-xs rounded-md border border-gray-200 pl-3 pr-6 py-1.5 font-popinsRegular text-darkColor focus:outline-none bg-white cursor-pointer"
            >
              {propertyOptions.map((p) => (
                <option key={p} value={p}>{p === "all" ? "All Properties" : p}</option>
              ))}
            </select>
          </div>

          <div className="overflow-x-auto w-full border border-gray-200 rounded-lg">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-200">
                  <th className="w-[22%] text-left py-3 pl-4">
                    <span className="flex items-center gap-1">
                      <ParagraphTag content="Tenant" classes="text-darkColor/60 font-popinsSemiBold text-xs" />
                      <SortIcon colKey="firstName" />
                    </span>
                  </th>
                  <th className="w-[20%] text-left py-3">
                    <span className="flex items-center gap-1">
                      <ParagraphTag content="Business Name" classes="text-darkColor/60 font-popinsSemiBold text-xs" />
                      <SortIcon colKey="businessName" />
                    </span>
                  </th>
                  <th className="w-[22%] text-left py-3">
                    <span className="flex items-center gap-1">
                      <ParagraphTag content="Property" classes="text-darkColor/60 font-popinsSemiBold text-xs" />
                      <SortIcon colKey="propertyName" />
                    </span>
                  </th>
                  <th className="w-[18%] text-left py-3">
                    <span className="flex items-center gap-1">
                      <ParagraphTag content="Phone Number" classes="text-darkColor/60 font-popinsSemiBold text-xs" />
                    </span>
                  </th>
                  <th className="w-[10%] text-right py-3 pr-4">
                    <ParagraphTag content={tenants.actions} classes="text-darkColor/60 font-popinsSemiBold text-xs" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, index) => (
                  <tr
                    key={item._id}
                    className={`border-b border-gray-200 cursor-pointer ${index % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-blue-50`}
                  >
                    <td className="py-3 pl-4">
                      <ParagraphTag
                        content={`TEN-${item?._id?.slice(0, 10)}`}
                        classes="text-darkColor/50 font-popinsMedium text-xs"
                      />
                      <ParagraphTag
                        content={`${item.firstName} ${item.lastName}`}
                        classes="text-darkColor font-popinsSemiBold text-sm"
                      />
                    </td>
                    <td className="py-3">
                      <ParagraphTag
                        content={item.businessName || "N/A"}
                        classes="text-darkColor font-popinsMedium text-sm"
                      />
                    </td>
                    <td className="py-3">
                      {item.propertyName ? (
                        <span className="inline-block bg-themeColor/10 text-themeColor font-popinsMedium text-xs px-2.5 py-1 rounded-full">
                          {item.propertyName}
                        </span>
                      ) : (
                        <ParagraphTag content="N/A" classes="text-darkColor/40 font-popinsRegular text-sm" />
                      )}
                    </td>
                    <td className="py-3">
                      <ParagraphTag
                        content={item.cellNo || "N/A"}
                        classes="text-darkColor font-popinsMedium text-sm"
                      />
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex justify-end">
                        <div
                          className="flex justify-center items-center border border-themeColor w-7 h-7 rounded-md cursor-pointer"
                          onClick={() => onViewDetails(item)}
                        >
                          <ArrowRight color="#6F6F6F" size={18} strokeWidth={2} />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {loading && <LeaseTableLoader />}
            {!loading && filteredData.length === 0 && (
              <div className="w-full flex justify-center mt-5 pb-5">
                <NoData />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-center items-center h-[13%]">
        <ReactPaginate
          previousLabel="Previous"
          nextLabel="Next"
          breakLabel="..."
          pageCount={Math.ceil(filteredData.length / itemsPerPage)}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={(p) => setCurrentPage(p.selected)}
          containerClassName="flex space-x-2 items-center"
          pageClassName="px-3 py-1 border rounded-md text-sm font-popinsMedium cursor-pointer"
          previousClassName="px-3 py-1 border rounded-l-md text-sm cursor-pointer font-popinsMedium"
          nextClassName="px-3 py-1 border rounded-r-md text-sm font-popinsMedium cursor-pointer"
          disabledClassName="opacity-50 !cursor-not-allowed"
          activeClassName="bg-themeColor text-white"
          breakClassName="px-3 py-1 text-sm font-popinsMedium"
        />
      </div>
    </div>
  );
};

export default TenantsTable;
